import {useState, useEffect, useCallback} from 'react'
import {Me, Division, LoginInfo} from "./api/api"
import { LOGIN_URL } from './links'
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./LoginHeader.scss"
import {api, useDivisionOrUndefined} from "./shared"
import {DUMMY_ACCESS_TOKEN, DUMMY_ACCESS_TOKEN_EXPIRES_IN}
from "./dummydata/api-constants"
import * as versiontxt from "./version.txt"

type Props = {
  me: Me | undefined
  dataRetrievedTime: Date | undefined
}

const SECOND = 1000 as const
const MINUTE = 60000 as const
const HOUR = 3600000 as const

const SECONDS_WAIT_BEFORE_NOTIFICATION = 10

const DATAAGE_ACKNOWLEDGE_INTERVAL = 1 * HOUR

function formatTime(seconds_or_minutes: number): string {
  const minutes_or_hours = Math.floor(seconds_or_minutes / 60)
  const secs = Math.round(seconds_or_minutes % 60)
  return `${minutes_or_hours}:${secs < 10 ? "0": ""}${secs}`
}

export function LoginHeader({me, dataRetrievedTime}: Props) {
  const divisionId = useDivisionOrUndefined()
  const [divisions, setDivisions] =
    useState<readonly Division[] | undefined>(undefined)
  const [version, setVersion] = useState("loading....")

  const [loginWindow, setLoginWindow] = useState<Window | null>(null)
  const [currentDivision, setCurrentDivision] =
    useState<Division | undefined>(undefined)
  const [now, setNow] = useState(Date.now())
  const [divisionSelectOpen, setDivisionSelectOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const [acknowledgedDataAge, setAcknowledgedDataAge] = useState(0)
  const dataAge = dataRetrievedTime === undefined ? undefined :
    Math.round((now - dataRetrievedTime.valueOf()) / MINUTE) * MINUTE
  const formattedDataAge = dataAge === undefined ? undefined :
    dataAge < HOUR ? `${dataAge/ MINUTE} minutes` :
      `${(dataAge / HOUR).toFixed(1)} hours`
  const showAcknowledgePopup =
    dataAge && (dataAge - acknowledgedDataAge >= DATAAGE_ACKNOWLEDGE_INTERVAL)

  const secondsUntilApiTokenExpires = (() => {
    const validTill = api.getApiTokenValidTill()
    return validTill === undefined ? undefined
      : Math.round((validTill - now) / SECOND)
  })()

  const nrWaitingForLogin = api.getNumberOfCallsWaitingForLogin()

  useEffect(() => {
    fetch(versiontxt.default).then(r => r.text()).then(t => setVersion(t))
  }, [])

  useEffect(() => {
    setAcknowledgedDataAge(0)
  }, [dataRetrievedTime])

  useEffect(() => {
    api.getDivisions().then(setDivisions)
  }, [])


  useEffect(() => {
    const loginInfoJson = window.sessionStorage.getItem("loginInfo")
    if (!loginInfoJson) {
      return
    }
    let loginInfoRaw
    try {
      loginInfoRaw = JSON.parse(loginInfoJson)
    } catch {
      console.log("JSON parse failed for loginInfo", loginInfoJson)
      return
    }
    const loginInfo: LoginInfo = {
      accessToken: loginInfoRaw.accessToken,
      validTill: new Date(loginInfoRaw.validTill)
    }
    if (loginInfo.validTill.valueOf() > new Date().valueOf() + 10*1000) {
      console.log("restore login")
      api.setLoginInfo(loginInfo).then(() => {
        console.log("Logged in.")
      }).catch(err => {
        alert("Error, login failed! Best to close this window and open a new one. "
          + err.message)
      })
    } else {
      console.log("Stored login has expired")
    }
  }, [])


  useEffect(function updateNow() {
    const timer = window.setInterval(() => {
      setNow(Date.now())
    }, 500)
    return () => window.clearInterval(timer)
  }, [])

  useEffect(function updateDivisionIfUnknownDivisionIsRequested() {
    if (!me || !divisions) {
      return
    }
    if (divisionId === undefined
        || !divisions.find(d => d.code === divisionId)) {
      // navigate to me's default division
      navigate(`/division/${me.currentDivision}`)
    } else {
      setCurrentDivision(divisions.find(d => d.code === divisionId)!)
    }
  }, [me, divisions, divisionId, navigate])

  const doLogin = useCallback((accessToken: string, validTill: Date) => {
    if (loginWindow) {
      loginWindow.close()
      setLoginWindow(null)
    }
    api.setLoginInfo({accessToken, validTill}).then(() => {
      window.sessionStorage.setItem("loginInfo", JSON.stringify({
        accessToken: accessToken,
        validTill: validTill.valueOf()
      }))
    }).catch(err => {
      alert("Error, login failed! Best to close this window and open a new one. "
        + err.message)
    })
  }, [loginWindow])

  useEffect(function listenForLoginInfoMessageFromPopupAndHandleIt() {
    if (!loginWindow) {
      return
    }
    type LoginInfoData = {
      loginInfo: LoginInfo
    }
    const handleLoginTokenMessage =
      ({data, origin, source}: MessageEvent<LoginInfoData>) => {
        const myorigin = new URL(window.location.toString()).origin
        if (origin !== myorigin) {
          console.log("Origin mismatch: " + origin + " != " + myorigin, {data})
          return
        }
        if (source !== loginWindow) {
          console.log("Source mismatch", {data})
          return
        }
        doLogin(data.loginInfo.accessToken, data.loginInfo.validTill)
    }
    window.addEventListener('message', handleLoginTokenMessage)
    return () => {window.removeEventListener("message", handleLoginTokenMessage)}
  }, [doLogin, loginWindow])

  useEffect(() => {
    setDivisionSelectOpen(false)
  }, [divisionId])

  useEffect(function closeDivisionSelectOnClick() {
    if (divisionSelectOpen) {
      const action = () => setDivisionSelectOpen(false);
      window.addEventListener("click", action)
      return () => window.removeEventListener("click", action)
    }
  }, [divisionSelectOpen])

  const getDivisionLink = (division: Division): string => {
    let pathitems = location.pathname.split("/")
    // "". "division", divisionId, ...
    pathitems[2] = division.code.toString()
    // TODO: if we add search params in the app, use them here
    return(pathitems.join("/"))
  }

  const startLoginSequence = () => {
    const url = document.querySelector("#loginButton")!.getAttribute("href")
      ?? LOGIN_URL
    setLoginWindow(window.open(url, "_blank", "popup"))
  }

  const notification_permission = Notification?.permission ?? "default"
  const waitingForRefresh = me
    && ((secondsUntilApiTokenExpires ?? 0) < -SECONDS_WAIT_BEFORE_NOTIFICATION)
    && (nrWaitingForLogin > 0)
  useEffect(() => {
    if (waitingForRefresh) {
      if (notification_permission === "granted") {
        new Notification(
          "Please refresh login in Stock & Backorders",
        {
          body: "There are API calls pending, but the login token expired",
          icon: "/logo512.png",
        })
      }
    }
  }, [notification_permission, waitingForRefresh])


  const notification_help = 'Switching notifications on and off happens in the settings menu of your browser, and can be set on a per-website basis. Google for "chrome notifications turn on" (or whatever browser you have) to change the settings.'

  return <div id="LoginInfo" className={nrWaitingForLogin ? "waitingForLogin" : ""}>
    <div id="divisionSelector">
    {me && divisions && currentDivision ?
      <>
        <span className="dropdownContainer">
          <span className="dropdownArrow"
          onClick={() => setDivisionSelectOpen(s => !s)}>
            <span id="CurrentDivision">
              {currentDivision.hid.toString()} - {currentDivision.description}
            </span>
          </span>
          <span
              className={"dropdownItem" + (divisionSelectOpen ? " open" : " closed")}>
            {divisions.map(division =>
              division.code === divisionId ?
              <span key={division.code.toString()} className="division">
                {division.hid.toString()} - {division.description}
              </span>
              :
              <Link className="division"
                  key={division.code.toString()} to={getDivisionLink(division)}>
                {division.hid.toString()} - {division.description}
              </Link>
            )}

          </span>
        </span>
      </>
    :
      <span className="dropdownContainer disabled">
        Choose a division
      </span>
    }
    </div>
    <div id="title">
      <img src="/logo192.png" alt="" />
      <span>Stock & Backorders</span>
    </div>
    <div id="nameSection" className={
      (secondsUntilApiTokenExpires ?? 0) < 0 ? "expired" : ""}>
      <div>
        {me ? (
          nrWaitingForLogin > 0 ?
            <span id="fullName" className="loggedin">
              {nrWaitingForLogin} api calls are paused until you log in again &#8594;
            </span> :
            <span id="fullName" className="loggedin"
              onClick={() => alert("In order to log out, close this window")}>
              {me.fullName}
            </span>
        )
      :
        <span id="fullName">Not logged in</span>
      }
      <span id="logoutTimer">{
        secondsUntilApiTokenExpires === undefined ?  "" :
        secondsUntilApiTokenExpires > 0 ? formatTime(secondsUntilApiTokenExpires) :
        "expired"
      }</span>
        <button id="loginButton" onClick={(e) => {
          if (api.isDummyApi() || (!me && e.shiftKey)) {
            doLogin(DUMMY_ACCESS_TOKEN,
              new Date(Date.now() + DUMMY_ACCESS_TOKEN_EXPIRES_IN * 1000))
          } else {
            startLoginSequence()
          }}}>
        {!me ? "Login" : "Refresh login"}
      </button>
      </div>
      <div>
        {notification_permission === "default" ?
        <button id="notification_permission" onClick={
          () => Notification.requestPermission()}>
          decide on notification permissions</button> :
          notification_permission === "granted" ?
            <span title={notification_help} id="notification_permission">notifications are on</span> :
            <span title={notification_help} id="notification_permission">notifications are off</span>
        }
        <span id="dataage">
          {formattedDataAge && <>Data is {formattedDataAge} old</>}
        </span>
        <span id="version">{version}</span>
      </div>
  </div>
    {!!(dataAge && showAcknowledgePopup) && <div id="acknowledgedDataAgePopup">
      You're working with data that
      is {Math.floor(dataAge / HOUR)} hours
      and {(dataAge % HOUR) / MINUTE} minutes old.
      Click acknowledge to continue, or reload this page to reload the data.
      <div>
        <button onClick={() => setAcknowledgedDataAge(dataAge)}>acknowledge</button>
        <button onClick={() => document.location.reload()}>reload</button>
      </div>
    </div>}
  </div>
}
